import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, CalendarDays, Clock3 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { API_URL } from "../data/content";
import { useLang } from "../i18n";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPost(null);
    setMissing(false);
    axios.get(`${API_URL}/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <section className="pt-48 pb-32 bg-cream text-center">
        <h1 className="font-display font-extrabold text-3xl text-forest">{t("post_missing")}</h1>
        <Link to="/blog" className="mt-4 inline-block font-bold text-tree hover:text-tangerine">{t("post_back")}</Link>
      </section>
    );
  }

  if (!post) {
    return <section className="pt-48 pb-32 bg-cream text-center text-bark">{t("blog_loading")}</section>;
  }

  const pick = (f) => (lang === "es" && post[`${f}_es`]) || post[f];
  const body = (lang === "es" && post.body_es) || post.body;

  return (
    <>
      <section className="pt-40 pb-10 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Reveal>
            <Link to="/blog" data-testid="blog-post-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-tree hover:text-tangerine transition-colors">
              <ArrowLeft className="w-4 h-4" /> {t("post_back")}
            </Link>
            <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.25em] text-tangerine">{pick("tag")}</p>
            <h1 data-testid="blog-post-title" className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-forest tracking-tight leading-tight">
              {pick("title")}
            </h1>
            <div className="mt-5 flex items-center gap-5 text-xs font-semibold text-bark/70">
              <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />
                {new Date(post.date + "T12:00:00").toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5" /> {lang === "es" ? post.read_time.replace("min read", "min de lectura") : post.read_time}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-forest/15 aspect-[16/8]">
              <img src={post.cover} alt={pick("title")} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <article data-testid="blog-post-body" className="bg-cream pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 space-y-6">
          {body.map((para, i) => (
            <Reveal key={`${lang}-${i}`} delay={Math.min(i * 0.03, 0.2)}>
              <p className={`text-charcoal/85 leading-relaxed ${i === 0 ? "text-lg sm:text-xl font-medium text-forest" : "text-base"}`}>
                {para}
              </p>
            </Reveal>
          ))}
        </div>
      </article>

      <section className="bg-forest text-cream py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">{t("post_cta_h2")}</h2>
            <p className="mt-3 text-cream/70">{t("post_cta_sub")}</p>
            <Link to="/contact" className="group mt-7 inline-flex items-center gap-2 rounded-full bg-amber text-charcoal px-8 py-4 font-display font-bold hover:bg-tangerine hover:text-white transition-colors shadow-xl">
              {t("post_cta_btn")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
